"use client";
import { useEffect, useCallback, memo, useRef, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import { getDefaultSizeIndex } from "@/app/lib/features/productDetail/productDetail.utils";
import ProductRating from "./more/ProductRating";
import ProductColors from "./more/ProductColors";
import ProductBadges from "./more/ProductBadges";
import SocialProofRotating from "./more/SocialProofRotating";
import ProductPrice from "./more/ProductPrice";
import ProductSizes from "./more/ProductSizes";
import DeliveryInfo from "./more/DeliveryInfo";
import ProductFeatures from "./more/ProductFeatures";
import ProductSizeTable from "./more/ProductSizeTable";
import MobileAppPromo from "./more/MobileAppPromo";
import ProductDisclaimer from "./more/ProductDisclaimer";
import ShowAllFeaturesButton from "./more/ShowAllFeaturesButton";
import ProductHeader from "./more/ProductHeader";
import ProductActions from "./more/ProductActions";
import SellerInfoMobile from "../productSidebar/more/SellerInfoMobile";

// useSearchParams Suspense boundary gerektiriyor — izole edilmezse üst Suspense
// (page.tsx'teki ProductDetailSkeleton fallback'i) tetiklenir ve tüm sayfa skeleton'a düşer.
const UrlParamSync = ({
  userSelectedRef,
}: {
  userSelectedRef: React.MutableRefObject<boolean>;
}) => {
  const searchParams = useSearchParams();
  const { product, selectedColorIndex, selectedSizeIndex, updateSelectedIndices } = useProductDetail();
  const vParam = searchParams.get("v");

  useEffect(() => {
    if (!product?.variants_by_color?.length) return;
    if (userSelectedRef.current) return;

    const currentColorIdx = product.variants_by_color.findIndex(
      (variant) => variant.slug === product.slug,
    );
    if (currentColorIdx === -1) return;

    const color = product.variants_by_color[currentColorIdx];

    let sizeIdx = -1;
    if (vParam) {
      const foundSizeIdx = color.sizes.findIndex((s) => s.size === vParam);
      if (foundSizeIdx !== -1) sizeIdx = foundSizeIdx;
    }
    // URL'de beden yoksa: tek bedenli varyantta o beden otomatik seçili gelir,
    // çok bedenlide seçim kullanıcıya bırakılır.
    if (sizeIdx === -1) sizeIdx = getDefaultSizeIndex(color?.sizes);

    if (selectedColorIndex !== currentColorIdx || selectedSizeIndex !== sizeIdx) {
      updateSelectedIndices(currentColorIdx, sizeIdx);
    }
  }, [product?.slug, product?.variants_by_color, vParam, updateSelectedIndices]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const ProductInformation = () => {
  const {
    product,
    selectedColorIndex,
    selectedSizeIndex,
    loadingColorIndex,
    updateSelectedIndices,
    updateLoadingColorIndex,
    updateIsColorChanging,
    fetchProductDetail,
  } = useProductDetail();

  // Yeni ürün yüklenince (renk değişimi tamamlanınca) loading state'i temizle
  useEffect(() => {
    if (product?.slug && loadingColorIndex !== null) {
      updateLoadingColorIndex(null);
      updateIsColorChanging(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.slug]);

  const userSelectedRef = useRef(false);

  // Renk değişiminde (yeni slug) manuel seçim bayrağını sıfırla
  useEffect(() => {
    userSelectedRef.current = false;
  }, [product?.slug]);

  const handleColorChange = useCallback(
    (colorIndex: number, slug: string) => {
      updateLoadingColorIndex(colorIndex);
      updateIsColorChanging(true);
      window.history.pushState(null, "", `/${slug}`);
      fetchProductDetail(slug);
    },
    [updateLoadingColorIndex, updateIsColorChanging, fetchProductDetail],
  );

  const handleSizeChange = useCallback(
    (sizeIndex: number) => {
      if (!product) return;

      const selectedColor = product.variants_by_color[selectedColorIndex];
      const selectedSize = selectedColor?.sizes?.[sizeIndex];
      if (!selectedSize) return;

      userSelectedRef.current = true;
      updateSelectedIndices(selectedColorIndex, sizeIndex);

      const newUrl =
        sizeIndex === 0
          ? `/${product.slug}`
          : `/${product.slug}?v=${selectedSize.size}`;
      window.history.replaceState(null, "", newUrl);
    },
    [product, selectedColorIndex, updateSelectedIndices],
  );

  if (!product) return null;

  return (
    <div className="space-y-2">
      <Suspense fallback={null}>
        <UrlParamSync userSelectedRef={userSelectedRef} />
      </Suspense>
      <ProductHeader />
      <ProductRating />
      <ProductBadges />
      <SocialProofRotating />
      <ProductPrice />
      <ProductColors
        selectedColorIndex={selectedColorIndex}
        loadingColorIndex={loadingColorIndex}
        onColorChange={handleColorChange}
      />
      <ProductSizes
        selectedColorIndex={selectedColorIndex}
        selectedSizeIndex={selectedSizeIndex}
        loadingSizeIndex={null}
        onSizeChange={handleSizeChange}
      />
      <div className="lg:hidden">
        <SellerInfoMobile />
      </div>
      <ProductActions
        selectedColorIndex={selectedColorIndex}
        selectedSizeIndex={selectedSizeIndex}
      />
      <div data-product-buttons aria-hidden="true" className="h-0" />
      <DeliveryInfo />
      <ProductFeatures />
      <ProductSizeTable />
      <MobileAppPromo />
      <ProductDisclaimer />
      <ShowAllFeaturesButton />
    </div>
  );
};

export default memo(ProductInformation);